// Shared helper functions for campaign applications
// Used by both dashboard and explore campaigns page

import { doc, getDoc, runTransaction, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import { APPLICATION_STATUSES } from './applicationStatus.js';

function toNumberOrNull(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Apply to a campaign
 * @param {Object} params
 * @param {string} params.campaignId - The campaign ID
 * @param {string} params.influencerId - The influencer user ID
 * @param {Object} params.campaignData - The campaign document data
 * @param {Object} params.influencerProfile - The influencer profile data
 * @param {string} params.influencerEmail - The influencer email
 * @returns {Promise<void>}
 */
export async function applyToCampaign({
  campaignId,
  influencerId,
  campaignData,
  influencerProfile,
  influencerEmail,
}) {
  if (!campaignId || !influencerId) {
    throw new Error('Campaign ID and influencer ID are required');
  }

  // Extract campaign details
  const campaignTitle =
    campaignData?.title ??
    campaignData?.name ??
    campaignData?.campaignName ??
    campaignData?.collaborationType ??
    'Untitled Campaign';
  
  const budgetUSD = toNumberOrNull(campaignData?.budgetUSD);
  const centsFromFields = toNumberOrNull(
    campaignData?.payment_cents ?? campaignData?.budget_cents
  );
  const dollarsField = toNumberOrNull(campaignData?.budget);
  const payCents =
    budgetUSD !== null
      ? Math.round(budgetUSD * 100)
      : centsFromFields !== null
      ? centsFromFields
      : dollarsField !== null
      ? Math.round(dollarsField * 100)
      : undefined;

  const appId = `${campaignId}_${influencerId}`;
  const rootRef = doc(db, 'applications', appId);
  const subRef = doc(db, 'campaigns', campaignId, 'applications', influencerId);
  const campaignRef = doc(db, 'campaigns', campaignId);

  await runTransaction(db, async (transaction) => {
    // Check if already applied (double-check)
    const existingApp = await transaction.get(rootRef);
    if (existingApp.exists()) {
      // Already applied, throw error
      throw new Error('You have already applied to this campaign');
    }

    // Optional: Check spots available
    const campSnap = await transaction.get(campaignRef);
    const campData = campSnap.exists() ? campSnap.data() : {};
    const appliedCount = campData.appliedCount || 0;
    const spotsAvailable = campaignData?.spotsAvailable;

    // If spots are tracked and all filled, throw error
    if (spotsAvailable !== undefined && appliedCount >= spotsAvailable) {
      throw new Error('No spots left for this campaign');
    }

    // Prepare application payload
    const applicationPayload = {
      id: appId,
      campaignId: campaignId,
      campaignTitle: campaignTitle,
      brandId: campaignData?.brandId ?? campaignData?.brandUid ?? null,
      brandName: campaignData?.brandName ?? null,
      influencerId: influencerId,
      influencerName: `${influencerProfile?.firstName || ''} ${influencerProfile?.lastName || ''}`.trim() || null,
      influencerEmail: influencerEmail || null,
      status: APPLICATION_STATUSES.PENDING_REVIEW,
      submittedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      source: 'influencer_portal',
      metrics: {
        followers: influencerProfile?.followers ?? influencerProfile?.totalFollowers ?? null,
        engagementRate: influencerProfile?.engagementRate ?? null,
        platforms: influencerProfile?.platforms ?? influencerProfile?.socialPlatforms ?? [],
      },
      snapshot: {
        title: campaignData?.title ?? null,
        bio: campaignData?.bio ?? null,
        collaborationType: campaignData?.collaborationType ?? null,
        budgetUSD: budgetUSD ?? (payCents !== undefined ? payCents / 100 : null),
        deadline: campaignData?.deadline ?? null,
        productLink: campaignData?.productLink ?? null,
        hasPhysicalGift: campaignData?.hasPhysicalGift ?? false,
        shippingFromCountry: campaignData?.shippingFromCountry ?? campaignData?.shippingFrom ?? null,
        shippingNotes: campaignData?.shippingNotes ?? null,
        revisionPolicy: campaignData?.revisionPolicy ?? null,
        spotsAvailable: spotsAvailable ?? null,
        // Legacy fields for backward compatibility
        payment: payCents !== undefined ? payCents : null,
      },
    };

    // Create root application document
    transaction.set(rootRef, applicationPayload);

    // Create mirror subcollection document
    transaction.set(subRef, applicationPayload);

    // Increment applied count on campaign
    transaction.set(campaignRef, { appliedCount: increment(1) }, { merge: true });
  });
}

/**
 * Check if an influencer has applied to a campaign
 * @param {string} campaignId - The campaign ID
 * @param {string} influencerId - The influencer user ID
 * @returns {Promise<boolean>}
 */
export async function hasAppliedToCampaign(campaignId, influencerId) {
  if (!campaignId || !influencerId) return false;
  
  try {
    const appId = `${campaignId}_${influencerId}`;
    const appRef = doc(db, 'applications', appId);
    const appSnap = await getDoc(appRef);
    return appSnap.exists();
  } catch (error) {
    console.error('Error checking application status:', error);
    return false;
  }
}

